/**
 * Gets the current date in DICOM format (YYYYMMDD).
 * @returns A string representing the current date in DICOM format.
 */
export function getDICOMDate(date = new Date()): string {
    const year = date.getFullYear().toString();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return year + month + day;
}

/**
 * Gets the current time in DICOM format (HHMMSS).
 * @returns A string representing the current time in DICOM format.
 */
export function getDICOMTime(): string {
    // get time in DICOM format: HHMMSS -> 225739
    const d = new Date(); // current time
    const h = d.getHours();
    const m = d.getMinutes();
    const s = d.getSeconds();

    const h_str = h < 10 ? '0' + h.toString() : h.toString();
    const m_str = m < 10 ? '0' + m.toString() : m.toString();
    const s_str = s < 10 ? '0' + s.toString() : s.toString();
    return h_str + m_str + s_str;
}

/**
 * Formats a resolved DICOM DateTime string (YYYYMMDDHHMMSS.FFFFFF) into
 * a human-readable time (HH:MM:SS).
 */
export function formatDicomTime(dicomDateTime: string): string {
    if (!dicomDateTime || dicomDateTime.length < 14) {
        return dicomDateTime ?? '—';
    }
    const hh = dicomDateTime.substring(8, 10);
    const mm = dicomDateTime.substring(10, 12);
    const ss = dicomDateTime.substring(12, 14);
    return `${hh}:${mm}:${ss}`;
}

/**
 * Parses a DICOM DateTime string (YYYYMMDDHHMMSS.FFFFFF) to an absolute
 * timestamp in seconds (UTC). Unlike {@link parseDicomTime}, this preserves
 * the date component, so elapsed-time calculations work across midnight.
 */
export function parseDicomDateTime(dateTimeStr: string): number {
    const str = dateTimeStr.trim();

    const year = parseInt(str.substring(0, 4), 10);
    const month = parseInt(str.substring(4, 6), 10) - 1; // JS months are 0-indexed
    const day = parseInt(str.substring(6, 8), 10);
    const hours = parseInt(str.substring(8, 10), 10) || 0;
    const minutes = parseInt(str.substring(10, 12), 10) || 0;
    const seconds = parseFloat(str.substring(12)) || 0;

    const wholeSec = Math.floor(seconds);
    const fracSec = seconds - wholeSec;

    const date = new Date(Date.UTC(year, month, day, hours, minutes, wholeSec));
    return date.getTime() / 1000 + fracSec;
}

export function parseDicomTime(timeStr: string): number {
    // DICOM time format: HHMMSS.FFFFFF or HHMMSS
    // DICOM datetime format: YYYYMMDDHHMMSS.FFFFFF
    const str = timeStr.trim();
    let timePart = str;

    // If it's a datetime (>= 14 chars with date prefix), extract time portion
    if (str.length >= 14 && !str.includes(':')) {
        timePart = str.substring(8);
    }

    const hours = parseInt(timePart.substring(0, 2), 10) || 0;
    const minutes = parseInt(timePart.substring(2, 4), 10) || 0;
    const seconds = parseFloat(timePart.substring(4)) || 0;

    return hours * 3600 + minutes * 60 + seconds;
}

export function getImageAge(seriesDate: Date | null): string {
    if (!seriesDate) {
        return 'no date';
    }

    const now = new Date();
    const diff = now.getTime() - seriesDate.getTime();

    // Convert milliseconds to different units
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const weeks = Math.floor(days / 7);
    const months = Math.floor(days / 30.44); // Average month length
    const years = Math.floor(days / 365.25); // Account for leap years

    // Return the most appropriate unit
    if (minutes < 60) {
        return `${minutes} minutes`;
    }
    if (hours < 24) {
        return `${hours} hours`;
    }
    if (days < 7) {
        return `${days} days`;
    }
    if (weeks < 4) {
        return `${weeks} weeks`;
    }
    if (months < 12) {
        return `${months} months`;
    }
    if (years > 0) {
        return `${years} ${years === 1 ? 'year' : 'years'}`;
    }
    return 'no date';
}

/** Insert a hardcoded preset header into a DICOM file. */
export function fixMissingMetaheader(arrayBuffer: ArrayBuffer): ArrayBuffer {
    console.warn(
        "NOTICE: Trying to load a DICOM file that doesn't have a metaheader. Inserting a preset header to get it to load."
    );

    const originalFile = new Uint8Array(arrayBuffer);

    // Required header elements:
    // * Empty preamble
    // * "DICM" prefix
    // * (0002,0000) *File Meta Information Group Length*, UL, 4
    // * (0002,0001) *File Meta Information Version*, OB, 2
    // * (0002,0002) *Media Storage SOP Class UID*, UI, N
    // * (0002,0003) *Media Storage SOP Instance UID*, UI, N
    // * (0002,0010) *Transfer Syntax UID*, UI, N
    // * (0002,0012) *Implementation Class UID*, UI, N

    const emptyPreamble = new Uint8Array(128);

    // NOTE: this metaheader is copied from an existing DICOM file. It WON'T match whatever file it is
    // being added to, but is usually enough to get dcmjs to load the file correctly.
    // NOTE 2: this array includes the DICM prefix
    const metaheader = new Uint8Array([
        0x44,
        0x49,
        0x43,
        0x4d,
        0x02,
        0x00,
        0x00,
        0x00,
        0x55,
        0x4c,
        0x04,
        0x00,
        0xd8,
        0x00,
        0x00,
        0x00,
        0x02,
        0x00,
        0x01,
        0x00,
        0x4f,
        0x42,
        0x00,
        0x00,
        0x02,
        0x00,
        0x00,
        0x00,
        0x00,
        0x01,
        0x02,
        0x00,
        0x02,
        0x00,
        0x55,
        0x49,
        0x1a,
        0x00,
        0x31,
        0x2e,
        0x32,
        0x2e,
        0x38,
        0x34,
        0x30,
        0x2e,
        0x31,
        0x30,
        0x30,
        0x30,
        0x38,
        0x2e,
        0x35,
        0x2e,
        0x31,
        0x2e,
        0x34,
        0x2e,
        0x31,
        0x2e,
        0x31,
        0x2e,
        0x32,
        0x00,
        0x02,
        0x00,
        0x03,
        0x00,
        0x55,
        0x49,
        0x2c,
        0x00,
        0x32,
        0x2e,
        0x32,
        0x35,
        0x2e,
        0x31,
        0x30,
        0x31,
        0x34,
        0x38,
        0x35,
        0x34,
        0x34,
        0x36,
        0x33,
        0x34,
        0x35,
        0x31,
        0x36,
        0x30,
        0x35,
        0x32,
        0x31,
        0x30,
        0x38,
        0x30,
        0x30,
        0x31,
        0x34,
        0x30,
        0x36,
        0x30,
        0x31,
        0x34,
        0x36,
        0x30,
        0x34,
        0x39,
        0x31,
        0x34,
        0x34,
        0x32,
        0x34,
        0x32,
        0x02,
        0x00,
        0x10,
        0x00,
        0x55,
        0x49,
        0x12,
        0x00,
        0x31,
        0x2e,
        0x32,
        0x2e,
        0x38,
        0x34,
        0x30,
        0x2e,
        0x31,
        0x30,
        0x30,
        0x30,
        0x38,
        0x2e,
        0x31,
        0x2e,
        0x32,
        0x00,
        0x02,
        0x00,
        0x12,
        0x00,
        0x55,
        0x49,
        0x40,
        0x00,
        0x32,
        0x2e,
        0x32,
        0x35,
        0x2e,
        0x38,
        0x30,
        0x33,
        0x30,
        0x32,
        0x38,
        0x31,
        0x33,
        0x31,
        0x33,
        0x37,
        0x37,
        0x38,
        0x36,
        0x33,
        0x39,
        0x38,
        0x35,
        0x35,
        0x34,
        0x37,
        0x34,
        0x32,
        0x30,
        0x35,
        0x30,
        0x39,
        0x32,
        0x36,
        0x37,
        0x33,
        0x34,
        0x36,
        0x33,
        0x30,
        0x39,
        0x32,
        0x31,
        0x36,
        0x30,
        0x33,
        0x33,
        0x36,
        0x36,
        0x36,
        0x34,
        0x38,
        0x32,
        0x32,
        0x35,
        0x32,
        0x31,
        0x32,
        0x31,
        0x34,
        0x35,
        0x34,
        0x30,
        0x34,
        0x02,
        0x00,
        0x13,
        0x00,
        0x53,
        0x48,
        0x0a,
        0x00,
        0x64,
        0x63,
        0x6d,
        0x6a,
        0x73,
        0x2d,
        0x30,
        0x2e,
        0x30,
        0x20, //0x08, 0x00, 0x05, 0x00, 0x0A, 0x00, 0x00, 0x00
    ]);

    const combined = new Uint8Array([
        ...emptyPreamble,
        ...metaheader,
        ...originalFile,
    ]);

    return combined.buffer;
}

/**
 * Formats a DICOM DateTime string (YYYYMMDDHHMMSS) into a
 * human-readable date (YYYY-MM-DD) or date-time (YYYY-MM-DD HH:MM:SS).
 * Returns '—' for missing or too-short input.
 */
export function formatDicomDateTime(dt: string | undefined): string {
    if (!dt || dt.length < 8) {
        return '—';
    }
    const y = dt.slice(0, 4);
    const m = dt.slice(4, 6);
    const d = dt.slice(6, 8);
    if (dt.length >= 14) {
        const hh = dt.slice(8, 10);
        const mm = dt.slice(10, 12);
        const ss = dt.slice(12, 14);
        return `${y}-${m}-${d} ${hh}:${mm}:${ss}`;
    }
    return `${y}-${m}-${d}`;
}

/**
 * Converts a gl-matrix mat4 (column-major) to a DICOM row-major
 * 16-element array for FrameOfReferenceTransformationMatrix.
 */
export function mat4ToRowMajorArray(m: ArrayLike<number>): number[] {
    return [
        m[0],
        m[4],
        m[8],
        m[12],
        m[1],
        m[5],
        m[9],
        m[13],
        m[2],
        m[6],
        m[10],
        m[14],
        m[3],
        m[7],
        m[11],
        m[15],
    ];
}

export function epsilonEqual(a: number, b: number, epsilon = 1e-6): boolean {
    return Math.abs(a - b) < epsilon;
}

export function roundToDecimals(a: number, decimals: number): number {
    const factor = Math.pow(10, decimals);
    return Math.round((a + Number.EPSILON) * factor) / factor;
}

export function extractRGBNumbers(rgbString: string): [number, number, number] {
    const regex = /rgb\((\d+),\s*(\d+),\s*(\d+)\)/;
    const matches = rgbString.match(regex);

    if (!matches) {
        throw new Error('Invalid RGB string format');
    }

    return matches.slice(1, 4).map(Number) as [number, number, number]; // Convert the matched strings to numbers and return as a tuple
}
